import z from 'zod';

import { ActivityZod } from './Activity';
import { AviationColorCodeZod } from './AviationColorCode';
import { CloudMovementZod } from './CloudMovement';
import { DurationZod } from './Duration';
import { HeightFromZod, HeightZod, limitHeightZod } from './Height';
import { StatusZod } from './Status';
import { isNotNullish } from './util/isNotNullish';
import { RequiredMessage } from './util/RequiredMessage';

export const UNKNOWN_VOLCANO_NUMBER = 'UNKNOWN';
export const UNKNOWN_VOLCANO_NAME = 'Unknown';

const AV_REGEXES = {
  notamCoordinates: /^(?<ns>[NS])(?<latd>\d{2})(?<latm>\d{2}) (?<ew>[EW])(?<lond>\d{2,3})(?<lonm>\d{2})$/,
  noticeNumber: /^20\d{2}\/\d{1,4}$/,
  unknownCoodinates: /^UNKNOWN$/i,
};

const AV_MAX_LENGTHS = {
  volcanoName: 50, // TODO: 19 char limit?
  volcanoArea: 50, // TODO: 16 char limit?
  svo: 256,
  cloudHeightSource: 32,
  remarks: 256,
  contacts: 128,
};

const parseCoordinates = (value: string) => {
  if (value.match(AV_REGEXES.unknownCoodinates)) {
    return null;
  }

  const match = value.match(AV_REGEXES.notamCoordinates);

  if (!match || !match.groups) {
    throw new Error(`Invalid coordinates format: ${value}`);
  }

  const latm = parseFloat(match.groups.latm);
  const lonm = parseFloat(match.groups.lonm);

  if (latm >= 60 || lonm >= 60) {
    throw new Error(`Invalid coordinates format: ${value}. Minutes must be less than 60.`);
  }

  const latSign = match.groups.ns === 'S' ? -1 : 1;
  const lat = (parseFloat(match.groups.latd) + (latm / 60.0)) * latSign;

  const lonSign = match.groups.ew === 'W' ? -1 : 1;
  const lon = (parseFloat(match.groups.lond) + (lonm / 60.0)) * lonSign;

  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    throw new Error(`Invalid coordinates format: ${value}. Coordinates out of bounds.`);
  }

  return {
    lat,
    lon,
    notamCoordinates: value,
  };
};

export const SummitHeightZod = limitHeightZod(HeightZod, -6000, 10000);

export const ApiVonaVolcanoZod = z.object({
  number: z.string().max(10).regex(/^\d+|UNKNOWN$/, { message: 'Volcano number must be an integer number or UNKNOWN' }),
  name: z.string().min(1, RequiredMessage).max(AV_MAX_LENGTHS.volcanoName),
  /** A single point indicating location of the voclano vent, or multiple
   * points indicating area. No coordinates means unknown */
  coordinates: z.array(z.string().superRefine((val, ctx) => {
    try {
      parseCoordinates(val);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Coordinates must be formatted using degrees and minutes (e.g. S2329 W06759)',
      });
    }
  })),
  area: z.string().min(1, RequiredMessage).max(AV_MAX_LENGTHS.volcanoArea),
  summitHeightAmsl: SummitHeightZod.or(z.literal('UNKNOWN')),
});

export const ApiUnlimitedCloudHeightZod = HeightFromZod.extend({
  /** Whether the cloud extends above the stated height. Used when top of
   * volcanic cloud is obscured by meteorological cloud. */
  extendsAbove: z.boolean(),
  source: z.string().min(1, RequiredMessage).max(AV_MAX_LENGTHS.cloudHeightSource),
});

export const ApiVonaCloudZod = z.object({
  /** Data about the volcanic cloud height. A null value indicates *unknown cloud height*. */
  height: limitHeightZod(ApiUnlimitedCloudHeightZod, -2000, 100000).nullable(),
  movement: CloudMovementZod,
});

export const ApiVonaZod = z.object({
  issuedAt: z.string().datetime(),
  status: StatusZod,
  volcano: ApiVonaVolcanoZod,
  colorCode: AviationColorCodeZod,
  previousColorCode: AviationColorCodeZod,
  svo: z.string().min(1, RequiredMessage).max(256),
  noticeNumber: z.string().regex(AV_REGEXES.noticeNumber, {
    message: 'Notice number must in the form YYYY/[N][N][N]N',
  }),
  activity: ActivityZod,
  onsetTime: z.union([
    z.string().datetime(),
    z.literal('UNKNOWN'),
    z.literal('NIL'),
  ]),
  duration: DurationZod,
  /** Data about the volcanic cloud. A null value indicates *no cloud present*. */
  cloud: ApiVonaCloudZod.nullable(),
  remarks: z.string().max(AV_MAX_LENGTHS.remarks).optional(),
  contacts: z.string().min(1, RequiredMessage).max(AV_MAX_LENGTHS.contacts),
  nextNotice: z.string().datetime().nullable(),
});

export type ApiVona = z.infer<typeof ApiVonaZod>;

export const ParsedApiVonaZod = ApiVonaZod.transform((vona) => ({
  ...vona,
  issuedAt: new Date(vona.issuedAt),
  volcano: {
    ...vona.volcano,
    coordinates: vona.volcano.coordinates.map((coords) => parseCoordinates(coords)).filter(isNotNullish),
  },
  activity: 'lastVaEmission' in vona.activity ? {
    ...vona.activity,
    lastVaEmission: new Date(vona.activity.lastVaEmission),
  } : vona.activity,
  onsetTime: (vona.onsetTime === 'UNKNOWN' || vona.onsetTime === 'NIL')
    ? vona.onsetTime
    : new Date(vona.onsetTime),
  nextNotice: vona.nextNotice ? new Date(vona.nextNotice) : null,
}));

export type ParsedApiVona = z.infer<typeof ParsedApiVonaZod>;
